// ============================================================
// Wauvea WebXR — Main Screen
// 180-200° curved cylindrical video screen
// ============================================================

import * as THREE from 'three';
import { CONFIG } from '../config.js';

export class MainScreen {
  constructor(scene) {
    this.scene = scene;
    this.mesh = null;
    this.videoElement = null;
    this.videoTexture = null;
    this.isPlaying = false;
    this._isPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
    this._timeCounter = 0;
    this._debugCounter = 0;
    // Dynamic glow (Ambilight)
    this._dynamicGlow = false;
    this._glowLights = [];
    this._glowCanvas = null;
    this._glowCtx = null;
    this._glowSampleCounter = 0;
    this._cornerRadius = 0;
    this._cornerAlphaMap = null;
  }

  /**
   * Build the curved main screen
   * @param {string} videoSrc - URL or path to video
   */
  build(videoSrc) {
    const { arcDegrees, height, radius, yPosition, segments } = CONFIG.mainScreen;
    const arcRad = THREE.MathUtils.degToRad(arcDegrees);
    const startAngle = -arcRad / 2 + Math.PI;

    // Curved cylinder geometry
    const geo = new THREE.CylinderGeometry(
      radius, radius, height, segments, 1, true, startAngle, arcRad
    );

    // Create video element if source provided
    if (videoSrc) {
      this._createVideoElement(videoSrc);
    }

    // Material — emissive so screen glows like a real display
    const mat = new THREE.MeshStandardMaterial({
      map: this.videoTexture || this._createFallbackTexture(),
      side: THREE.BackSide,
      emissive: new THREE.Color(0xffffff),
      emissiveMap: this.videoTexture || null,
      emissiveIntensity: 0.4,
      roughness: 0.3,
      metalness: 0.1,
    });

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.y = yPosition;
    this.mesh.name = 'mainScreen';
    this.scene.add(this.mesh);

    // Auto-play if video source was provided
    if (videoSrc) {
      this.play();
    }
  }

  _createVideoElement(src) {
    // Remove any previous video element from the DOM
    if (this.videoElement && this.videoElement.parentNode) {
      this.videoElement.parentNode.removeChild(this.videoElement);
    }

    this.videoElement = document.createElement('video');
    this.videoElement.src = src;
    // Only set crossOrigin for absolute URLs
    if (src.startsWith('http://') || src.startsWith('https://')) {
      this.videoElement.crossOrigin = 'anonymous';
    }
    this.videoElement.loop = true;
    this.videoElement.muted = true;
    this.videoElement.playsInline = true;
    this.videoElement.preload = 'auto';
    // CRITICAL: Append to DOM so visionOS Safari doesn't treat as "background media"
    // and pause it to save power. Hidden via CSS.
    this.videoElement.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(this.videoElement);

    this._mediaStatus = 'loading';
    this.isBuffering = true;
    if (this._isPreview) {
      window.parent.postMessage({ type: 'mediaStatus', payload: { status: 'loading', src } }, '*');
    }

    const BUFFER_THRESHOLD = 5; // seconds to buffer before playing

    // Check if enough is buffered to play smoothly
    const checkBuffer = () => {
      if (!this.videoElement) return 0;
      const buf = this.videoElement.buffered;
      if (buf.length === 0) return 0;
      const ct = this.videoElement.currentTime || 0;
      for (let i = 0; i < buf.length; i++) {
        if (buf.start(i) <= ct && buf.end(i) > ct) {
          return buf.end(i) - ct;
        }
      }
      return 0;
    };

    const tryPlay = () => {
      const buffered = checkBuffer();
      if (buffered >= BUFFER_THRESHOLD || this.videoElement.readyState >= 4) {
        this.isBuffering = false;
        this._mediaStatus = 'ready';
        console.log('[MainScreen] Buffer OK:', buffered.toFixed(1) + 's buffered, starting playback');
        if (this._isPreview) {
          window.parent.postMessage({ type: 'mediaStatus', payload: { status: 'ready', src } }, '*');
        }
        if (this.videoElement.paused) {
          this.videoElement.play().then(() => {
            this.isPlaying = true;
            console.log('[MainScreen] ▶ Playing');
          }).catch(e => console.error('[MainScreen] Play failed:', e.message));
        }
      }
    };

    // Monitor buffer progress
    this.videoElement.addEventListener('progress', () => {
      if (this.isBuffering) {
        const buffered = checkBuffer();
        console.log('[MainScreen] Buffering...', buffered.toFixed(1) + 's /' + BUFFER_THRESHOLD + 's');
        tryPlay();
      }
    });

    this.videoElement.addEventListener('canplay', () => {
      console.log('[MainScreen] canplay, readyState:', this.videoElement.readyState);
      tryPlay();
    }, { once: true });

    // Handle stalls — pause and re-buffer instead of choppy playback
    this.videoElement.addEventListener('waiting', () => {
      if (!this.isBuffering) {
        console.log('[MainScreen] ⏳ Stalled — re-buffering');
        this.isBuffering = true;
        this._mediaStatus = 'buffering';
      }
    });

    // Resume once enough data arrives after a stall
    this.videoElement.addEventListener('playing', () => {
      if (this.isBuffering) {
        this.isBuffering = false;
        this._mediaStatus = 'ready';
        console.log('[MainScreen] ▶ Resumed from buffer');
      }
    });

    this.videoElement.addEventListener('error', (e) => {
      this._mediaStatus = 'error';
      const err = this.videoElement?.error;
      console.error('[MainScreen] Video load error:', err?.message || 'unknown', '\n  URL:', src);
      if (this._isPreview) {
        window.parent.postMessage({ type: 'mediaStatus', payload: { status: 'error', src, error: err?.message } }, '*');
      }
    }, { once: true });

    this.videoTexture = new THREE.VideoTexture(this.videoElement);
    this.videoTexture.colorSpace = THREE.SRGBColorSpace;
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;

    this.videoElement.load();
  }

  _createFallbackTexture() {
    // Dark screen with subtle gradient when no video loaded
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#1a1a3a');
    gradient.addColorStop(1, '#0a0a1a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Center text
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.font = 'bold 24px Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('MAIN STAGE', canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  play() {
    if (this.videoElement && this.videoElement.paused) {
      console.log('[MainScreen] play() called, readyState:', this.videoElement.readyState,
        'src:', (this.videoElement.src || '').substring(0, 60));
      this.videoElement.play().then(() => {
        this.isPlaying = true;
        console.log('[MainScreen] ▶ Playing successfully');
      }).catch((e) => {
        console.error('[MainScreen] ▶ Play FAILED:', e.name, e.message);
      });
    } else {
      console.log('[MainScreen] play() skipped — no videoElement or already playing',
        'hasVideo:', !!this.videoElement, 'paused:', this.videoElement?.paused);
    }
  }

  pause() {
    if (this.videoElement && !this.videoElement.paused) {
      this.videoElement.pause();
      this.isPlaying = false;
    }
  }

  togglePlay() {
    if (this.videoElement?.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  toggleMute() {
    if (this.videoElement) {
      this.videoElement.muted = !this.videoElement.muted;
    }
  }

  get isMuted() {
    return this.videoElement?.muted ?? true;
  }

  get currentTime() {
    return this.videoElement?.currentTime ?? 0;
  }

  get duration() {
    return this.videoElement?.duration ?? 0;
  }

  seek(time) {
    if (this.videoElement && this.videoElement.duration) {
      this.videoElement.currentTime = time;
    }
  }

  update() {
    if (this.videoTexture && this.isPlaying) {
      this.videoTexture.needsUpdate = true;
    }
    // Periodic video state debug (~once per 3 seconds at 90fps)
    if (this.videoElement && ++this._debugCounter % 270 === 0) {
      console.log('[MainScreen] state:', 
        'playing:', this.isPlaying,
        'paused:', this.videoElement.paused,
        'readyState:', this.videoElement.readyState,
        'currentTime:', this.videoElement.currentTime?.toFixed(1),
        'hasTexture:', !!this.videoTexture);
    }
    // Dynamic glow — sample video colors onto edge lights
    if (this._dynamicGlow && this._glowLights.length > 0) {
      this._sampleVideoForGlow();
    }
    // Report playback position to parent (throttled ~4fps)
    if (this.videoElement && this._isPreview && ++this._timeCounter % 15 === 0) {
      window.parent.postMessage({ type: 'timeUpdate', payload: {
        currentTime: this.videoElement.currentTime || 0,
        duration: this.videoElement.duration || 0,
        playing: !this.videoElement.paused,
      }}, '*');
    }
  }

  /**
   * Swap the video source at runtime (called from preview postMessage)
   * @param {string} newSrc - New video URL
   */
  setVideoSource(newSrc) {
    // Handle case where newSrc is an object { s3Key, ... } instead of a string
    let url = newSrc;
    if (typeof newSrc === 'object' && newSrc !== null) {
      url = newSrc.s3Key || newSrc.url || '';
    }
    console.log('[MainScreen] setVideoSource:', url);
    if (!url) return;

    if (this.videoElement) {
      this.videoElement.pause();
    }
    this._createVideoElement(url);
    if (this.mesh) {
      this.mesh.material.map = this.videoTexture;
      this.mesh.material.emissiveMap = this.videoTexture;
      this.mesh.material.needsUpdate = true;
    }
    this.play();
  }

  /**
   * Rebuild screen geometry with new parameters (called by CalibrationPanel)
   */
  rebuild(scene, { projection, arcDegrees, radius, height: customHeight, screenArc, screenY }) {
    // Preserve video texture before disposing material
    const savedTexture = this.videoTexture;

    if (this.mesh) {
      scene.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh.material.dispose();
    }

    const height = customHeight || CONFIG.mainScreen.height;
    const yPosition = CONFIG.mainScreen.yPosition;
    const segments = CONFIG.mainScreen.segments;
    const baseRadius = radius || CONFIG.mainScreen.radius;

    // Determine curvature from screenArc (overrides projection if provided)
    const isFlat = screenArc != null ? screenArc <= 5 : projection === 'flat';
    const arc = arcDegrees || CONFIG.mainScreen.arcDegrees;

    let geo, side;
    let effectiveRadius = baseRadius;
    if (isFlat) {
      // Flat plane screen
      const width = (arc / 180) * Math.PI * baseRadius;
      geo = new THREE.PlaneGeometry(width, height, segments, 1);
      side = THREE.DoubleSide;
    } else {
      // Curved cylinder — screenArc controls curvature via radius interpolation
      const maxR = 120;
      const minR = Math.max(baseRadius, 15);
      const sArc = screenArc != null ? screenArc : 180;
      const t = Math.min((sArc - 5) / 355, 1);
      effectiveRadius = maxR - t * (maxR - minR);
      // Keep visual width constant
      const desiredArcLength = (arc / 180) * Math.PI * baseRadius;
      const thetaLength = Math.min(desiredArcLength / effectiveRadius, 2 * Math.PI);
      const startAngle = -thetaLength / 2 + Math.PI;
      geo = new THREE.CylinderGeometry(
        effectiveRadius, effectiveRadius, height, segments, 1, true, startAngle, thetaLength
      );
      side = THREE.BackSide;
    }

    const mat = new THREE.MeshStandardMaterial({
      map: savedTexture || this._createFallbackTexture(),
      side: side,
      emissive: new THREE.Color(0xffffff),
      emissiveMap: savedTexture || null,
      emissiveIntensity: 0.4,
      roughness: 0.3,
      metalness: 0.1,
    });

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.position.y = screenY != null ? screenY : yPosition;
    this.mesh.name = 'mainScreen';

    if (isFlat) {
      // Flat: place plane at fixed distance
      this.mesh.position.z = -baseRadius;
    } else {
      // Curved: offset center so front surface stays at z = -baseRadius
      this.mesh.position.z = effectiveRadius - baseRadius;
    }

    scene.add(this.mesh);

    // Reapply corner radius if previously set
    if (this._cornerRadius > 0) {
      this.setCornerRadius(this._cornerRadius);
    }

    console.log('[MainScreen] Rebuilt: screenArc=' + (screenArc ?? 'n/a') + ' arc=' + arc + ' isFlat=' + isFlat);
  }

  // ─── Corner Rounding ───────────────────────────────────────────
  /**
   * Apply rounded corners to the main screen via an alpha mask texture.
   * @param {number} radius — 0 = sharp, 1–10 = graduated rounding
   */
  setCornerRadius(radius) {
    if (!this.mesh) return;
    this._cornerRadius = radius;

    if (radius <= 0) {
      this.mesh.material.alphaMap = null;
      this.mesh.material.transparent = false;
      this.mesh.material.needsUpdate = true;
      return;
    }

    // Generate alpha mask with rounded corners
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // White rounded rect = opaque area
    const r = Math.min(radius * 40, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.roundRect(0, 0, canvas.width, canvas.height, r);
    ctx.fill();

    if (this._cornerAlphaMap) this._cornerAlphaMap.dispose();
    this._cornerAlphaMap = new THREE.CanvasTexture(canvas);

    this.mesh.material.alphaMap = this._cornerAlphaMap;
    this.mesh.material.transparent = true;
    this.mesh.material.needsUpdate = true;
  }

  // ─── Ambient Glow (Movie-Theater Edge Lighting) ────────────────
  /**
   * Add PointLights around the screen perimeter that illuminate surrounding
   * surfaces, simulating a movie-theater screen glow effect.
   * @param {string|number} color — Light colour (hex string or int)
   * @param {number} intensity — 0 = off, 0.1–5 = graduated
   */
  setAmbientGlow(color, intensity) {
    // Remove previous glow lights
    if (this._glowLights) {
      this._glowLights.forEach(l => { this.scene.remove(l); l.dispose(); });
    }
    this._glowLights = [];

    if (!intensity || intensity <= 0) return;

    const { arcDegrees, height, radius, yPosition } = CONFIG.mainScreen;
    const arcRad = THREE.MathUtils.degToRad(arcDegrees);
    const lightColor = new THREE.Color(color);
    const lightRadius = radius + 1.5;   // Just outside the screen surface
    const decay = 2;
    const distance = 25;

    // ── Top & bottom edge lights ──
    const numPerEdge = 5;
    for (let i = 0; i <= numPerEdge; i++) {
      const t = i / numPerEdge;
      const angle = -arcRad / 2 + t * arcRad + Math.PI;
      const x = Math.sin(angle) * lightRadius;
      const z = Math.cos(angle) * lightRadius;

      // Top
      const top = new THREE.PointLight(lightColor, intensity * 0.5, distance, decay);
      top.position.set(x, yPosition + height / 2 + 1, z);
      top.name = 'screenGlow';
      this.scene.add(top);
      this._glowLights.push(top);

      // Bottom
      const bot = new THREE.PointLight(lightColor, intensity * 0.5, distance, decay);
      bot.position.set(x, yPosition - height / 2 - 1, z);
      bot.name = 'screenGlow';
      this.scene.add(bot);
      this._glowLights.push(bot);
    }

    // ── Left & right edge lights ──
    const numVertical = 3;
    for (let i = 0; i <= numVertical; i++) {
      const t = i / numVertical;
      const y = yPosition - height / 2 + t * height;

      // Left edge
      const la = -arcRad / 2 + Math.PI;
      const left = new THREE.PointLight(lightColor, intensity * 0.3, distance, decay);
      left.position.set(Math.sin(la) * lightRadius, y, Math.cos(la) * lightRadius);
      left.name = 'screenGlow';
      this.scene.add(left);
      this._glowLights.push(left);

      // Right edge
      const ra = arcRad / 2 + Math.PI;
      const right = new THREE.PointLight(lightColor, intensity * 0.3, distance, decay);
      right.position.set(Math.sin(ra) * lightRadius, y, Math.cos(ra) * lightRadius);
      right.name = 'screenGlow';
      this.scene.add(right);
      this._glowLights.push(right);
    }
  }

  // ─── Dynamic Glow (Ambilight) ──────────────────────────────────
  /**
   * Enable/disable dynamic video color sampling for glow lights.
   * When enabled, the glow lights automatically sample the playing
   * video's edge colors — like Philips Ambilight / theater wash.
   * @param {boolean} enabled
   */
  enableDynamicGlow(enabled) {
    this._dynamicGlow = enabled;
    if (enabled && !this._glowCanvas) {
      this._glowCanvas = document.createElement('canvas');
      this._glowCanvas.width = 16;   // tiny — we only need edge colors
      this._glowCanvas.height = 8;
      this._glowCtx = this._glowCanvas.getContext('2d', { willReadFrequently: true });
    }
  }

  /** @private Sample the playing video and update glow light colours. */
  _sampleVideoForGlow() {
    if (!this.videoElement || this.videoElement.paused || !this.videoElement.videoWidth) return;

    // ~5 fps sampling (every 18th render frame at 90fps in XR)
    // Keeps GPU→CPU readback overhead minimal for smooth head tracking
    if (++this._glowSampleCounter % 18 !== 0) return;

    const ctx = this._glowCtx;
    const w = this._glowCanvas.width;
    const h = this._glowCanvas.height;

    try {
      ctx.drawImage(this.videoElement, 0, 0, w, h);
    } catch (_e) { return; } // CORS or detached video

    const data = ctx.getImageData(0, 0, w, h).data;
    const pix = (x, y) => {
      const i = (y * w + x) * 4;
      return new THREE.Color(data[i] / 255, data[i + 1] / 255, data[i + 2] / 255);
    };

    // Layout of _glowLights (from setAmbientGlow):
    //   [0..11] top/bottom pairs  (6 pairs, i*2 = top, i*2+1 = bottom)
    //   [12..19] left/right pairs (4 pairs, j*2 = left, j*2+1 = right)
    const numPerEdge = 5;
    const topBottomCount = (numPerEdge + 1) * 2; // 12

    // Top & bottom edge colours
    for (let i = 0; i <= numPerEdge; i++) {
      const x = Math.round(i / numPerEdge * (w - 1));
      const topIdx = i * 2;
      const botIdx = i * 2 + 1;
      if (topIdx < this._glowLights.length) this._glowLights[topIdx].color.copy(pix(x, 0));
      if (botIdx < this._glowLights.length) this._glowLights[botIdx].color.copy(pix(x, h - 1));
    }

    // Left & right edge colours
    const numVertical = 3;
    for (let j = 0; j <= numVertical; j++) {
      const y = Math.round(j / numVertical * (h - 1));
      const lIdx = topBottomCount + j * 2;
      const rIdx = topBottomCount + j * 2 + 1;
      if (lIdx < this._glowLights.length) this._glowLights[lIdx].color.copy(pix(0, y));
      if (rIdx < this._glowLights.length) this._glowLights[rIdx].color.copy(pix(w - 1, y));
    }
  }

  // ─── Main Screen Base Colour ───────────────────────────────────
  /**
   * Set the base colour of the main screen (when no video is playing).
   * @param {string|number} color — hex string or int
   */
  setMainScreenColor(color) {
    if (!this.mesh) return;
    const c = new THREE.Color(color);
    this.mesh.material.color.set(c);
    this.mesh.material.emissive.set(c);
    this.mesh.material.needsUpdate = true;
  }

  /**
   * Show/hide glow lights (used by adaptive quality during fast turns)
   */
  setGlowVisible(visible) {
    this._glowLights.forEach(l => { l.visible = visible; });
  }
}
